
<template>
  <div class="rb-page-header">
    <nav v-if="showRoutePath" class="rb-breadcrumbs" aria-label="Breadcrumb">
      <template
        v-for="(crumb, index) in breadcrumbItems"
        :key="index"
      >
        <span
          class="breadcrumb-text"
          :class="{
            'breadcrumb-current': index === breadcrumbItems.length - 1
          }"
          :aria-current="
            index === breadcrumbItems.length - 1 ? 'page' : undefined
          "
        >
          <v-icon v-if="index === 0" size="14">
            mdi-home-outline
          </v-icon>
          {{ crumb.title }}
        </span>

        <v-icon
          v-if="index < breadcrumbItems.length - 1"
          size="13"
          class="breadcrumb-separator"
        >
          mdi-chevron-right
        </v-icon>
      </template>
    </nav>

    <div class="header-main">
      <div class="header-info">
        <div class="header-icon">
          <v-icon size="19">
            {{ icon }}
          </v-icon>
        </div>

        <div class="header-text">
          <h1 class="page-title">
            {{ title }}
          </h1>

          <p v-if="computedSubtitle" class="page-subtitle">
            {{ computedSubtitle }}
          </p>
        </div>
      </div>

      <div class="header-actions">
        <slot name="actions" />

        <v-btn
          v-if="title && isAddBtn"
          class="add-button"
          color="primary"
          variant="flat"
          prepend-icon="mdi-plus"
          size="small"
          @click="$emit('add')"
        >
          {{ computedBtnText }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "RbTitleToolbar",

  emits: ["add"],

  props: {
    title: {
      type: String,
      default: "",
    },
    
    showRoutePath : {
      type: Boolean,
      default: true,
    },

    subtitle: {
      type: String,
      default: "",
    },

    btnText: {
      type: String,
      default: "",
    },

    isAddBtn: {
      type: Boolean,
      default: true,
    },

    icon: {
      type: String,
      default: "mdi-view-dashboard-outline",
    },

    breadcrumbs: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    computedSubtitle() {
      return (
        this.subtitle ||
        `Manage and review all ${this.title} processed through the system.`
      );
    },

    computedBtnText() {
      return this.btnText || `Add ${this.title}`;
    },

    breadcrumbItems() {
      if (this.breadcrumbs.length) {
        return this.breadcrumbs;
      }

      return [
        { title: "Home" },
        { title: this.title },
      ];
    },
  },
};
</script>

<style scoped>
.rb-page-header {
  color: rgb(var(--v-theme-on-background));
}

.rb-breadcrumbs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  min-height: 18px;
  margin-bottom: 22px;
}

.breadcrumb-text {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: rgb(var(--v-theme-rb-muted));
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  cursor: default;
}

.breadcrumb-current {
  color: rgb(var(--v-theme-rb-text));
  font-weight: 600;
}

.breadcrumb-separator {
  color: rgb(var(--v-theme-rb-muted));
  opacity: 0.7;
}

.header-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border: 1px solid rgb(var(--v-theme-rb-border));
  border-radius: 9px;
  background: rgb(var(--v-theme-rb-primary-light));
  color: rgb(var(--v-theme-rb-sidebar-active-text));
}

.header-text {
  min-width: 0;
}

.page-title {
  margin: 0;
  color: rgb(var(--v-theme-rb-text));
  font-size: 19px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.3px;
}

.page-subtitle {
  margin: 3px 0 0;
  color: rgb(var(--v-theme-rb-muted));
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.add-button {
  height: 34px !important;
  min-width: 0 !important;
  padding: 0 13px !important;
  border-radius: 7px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  box-shadow: 0 2px 6px rgba(255, 176, 0, 0.14) !important;
  transition: box-shadow 0.2s ease;
}

.add-button :deep(.v-btn__prepend) {
  margin-inline-end: 5px;
}

.add-button :deep(.v-btn__prepend .v-icon) {
  font-size: 16px;
  color: #000000 !important;
}

.add-button:hover,
.add-button:focus,
.add-button:active {
  background: #FFB000 !important;
  color: #000000 !important;
  box-shadow: 0 4px 10px rgba(255, 176, 0, 0.2) !important;
}

.add-button :deep(.v-btn__overlay) {
  opacity: 0 !important;
}

@media (max-width: 600px) {
  .rb-page-header {
    padding: 4px 0 12px;
  }

  .rb-breadcrumbs {
    margin-bottom: 9px;
  }

  .header-main {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 12px;
  }

  .header-icon {
    width: 34px;
    height: 34px;
  }

  .page-title {
    font-size: 18px;
  }

  .page-subtitle {
    font-size: 11.5px;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .add-button {
    height: 34px !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  .add-button {
    transition: none;
  }
}
</style>